import { create } from 'zustand';
import { api, ApiError, onSessionRejected } from '@/api/client';
import type { MemberSummary } from '@/api/types';
import { tabs } from '@/realtime/tabChannel';

/**
 * Who is signed in on this browser (data-model.md B1). The session itself is
 * an httpOnly cookie the page never sees; this store only knows the result:
 * rebuilt from `GET /accounts/me/profile` on load, and kept in step across
 * tabs (WEB-011) and with the server (a 401 ends it, as on Android).
 */
export type SessionStatus = 'unknown' | 'signedOut' | 'signedIn';
/** Why a session ended without the member signing out (shown on the sign-in page). */
export type SessionEndedReason = 'expired' | 'inactive';

interface SessionState {
  status: SessionStatus;
  member: MemberSummary | null;
  endedReason: SessionEndedReason | null;
  /** The member signed out in this browser (log out, deleted account, another tab): Welcome, not sign-in. */
  signedOutByMember?: boolean;
  /** The last bootstrap could not reach the server; the app shows a retry. */
  bootstrapFailed?: boolean;
  bootstrap: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<MemberSummary>;
  /**
   * Enters the app as `member` after the session cookie was set elsewhere
   * (e.g. the pledge, which signs in and accepts the pledge first), and tells the other tabs.
   */
  establish: (member: MemberSummary) => void;
  signOut: () => Promise<void>;
  /**
   * The account was just deleted (FR-006): its session is gone with it, so this
   * browser forgets it at once, without asking the server, and never as an
   * expired session.
   */
  forgetDeletedAccount: () => void;
  setMember: (member: MemberSummary) => void;
}

/** localStorage keys that survive sign-out: device preferences, never personal data. */
const DEVICE_KEYS = new Set(['kindra.prefs']);
const cleanups = new Set<() => void>();

const beforeSignOut = new Set<() => Promise<void>>();
const BEFORE_SIGN_OUT_TIMEOUT_MS = 3000;

/**
 * Runs while the session is still valid, just before an explicit sign-out
 * (e.g. removing this browser's push token, which needs the session). Slow or
 * failing tasks never keep the member from signing out.
 */
export function registerBeforeSignOut(task: () => Promise<void>): () => void {
  beforeSignOut.add(task);
  return () => beforeSignOut.delete(task);
}

function runBeforeSignOut(): Promise<unknown> {
  return Promise.allSettled(
    [...beforeSignOut].map((task) =>
      Promise.race([
        task(),
        new Promise<void>((resolve) => setTimeout(resolve, BEFORE_SIGN_OUT_TIMEOUT_MS)),
      ]),
    ),
  );
}

/** Runs on every sign-out on this tab (e.g. clearing the query cache, stopping push). */
export function registerSignOutCleanup(cleanup: () => void): () => void {
  cleanups.add(cleanup);
  return () => cleanups.delete(cleanup);
}

/**
 * Forgets this member's data in this tab. `keepDrafts` keeps sessionStorage
 * (half-written forms) when a session merely expired, so the member can sign in
 * again and carry on; an explicit sign-out clears everything (WEB-021).
 */
function clearLocalData({ keepDrafts = false }: { keepDrafts?: boolean } = {}): void {
  cleanups.forEach((cleanup) => {
    try {
      cleanup();
    } catch {
      // one failing cleanup must not keep data from being cleared
    }
  });
  try {
    if (!keepDrafts) {
      sessionStorage.clear();
    }
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('kindra.') && !DEVICE_KEYS.has(key)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // storage can be unavailable (private mode); nothing to clear then
  }
}

export const useSessionStore = create<SessionState>()((set, get) => ({
  status: 'unknown',
  member: null,
  endedReason: null,
  signedOutByMember: false,

  bootstrap: async () => {
    try {
      const { member } = await api.get<{ member: MemberSummary }>('/accounts/me/profile');
      set({ status: 'signedIn', member, bootstrapFailed: false });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // Suspended or removed since the last visit: say so on the sign-in page.
        const inactive = /no longer active/i.test(error.message);
        set({
          status: 'signedOut',
          member: null,
          bootstrapFailed: false,
          ...(inactive ? { endedReason: 'inactive' as const } : {}),
        });
      } else {
        set({ bootstrapFailed: true });
      }
    }
  },

  signIn: async (email, password) => {
    const { member } = await api.post<{ member: MemberSummary }>('/auth/login', {
      email,
      password,
    });
    get().establish(member);
    return member;
  },

  establish: (member) => {
    set({
      status: 'signedIn',
      member,
      endedReason: null,
      bootstrapFailed: false,
      signedOutByMember: false,
    });
    tabs.post({ type: 'session:signed-in' });
  },

  signOut: async () => {
    await runBeforeSignOut();
    try {
      await api.post('/auth/logout');
    } catch {
      // The cookie may already be gone; this browser forgets the session regardless.
    }
    clearLocalData();
    set({ status: 'signedOut', member: null, endedReason: null, signedOutByMember: true });
    tabs.post({ type: 'session:signed-out' });
  },

  forgetDeletedAccount: () => {
    clearLocalData();
    set({ status: 'signedOut', member: null, endedReason: null, signedOutByMember: true });
    tabs.post({ type: 'session:signed-out' });
  },

  setMember: (member) => {
    if (get().status === 'signedIn') {
      set({ member });
    }
  },
}));

/**
 * Keeps this tab's session in step with the other tabs and with the server.
 * Started once by the app's providers; returns a stop function.
 */
export function startSessionSync(): () => void {
  const stopTabs = tabs.subscribe((message) => {
    if (
      message.type === 'session:signed-out' &&
      useSessionStore.getState().status !== 'signedOut'
    ) {
      clearLocalData();
      useSessionStore.setState({
        status: 'signedOut',
        member: null,
        endedReason: null,
        signedOutByMember: true,
      });
    }
    if (message.type === 'session:signed-in') {
      void useSessionStore.getState().bootstrap();
    }
  });
  const stopRejected = onSessionRejected(({ message }) => {
    if (useSessionStore.getState().status !== 'signedIn') {
      return;
    }
    const endedReason = /no longer active/i.test(message) ? 'inactive' : 'expired';
    clearLocalData({ keepDrafts: endedReason === 'expired' });
    useSessionStore.setState({
      status: 'signedOut',
      member: null,
      endedReason,
      signedOutByMember: false,
    });
    tabs.post({ type: 'session:signed-out' });
  });
  return () => {
    stopTabs();
    stopRejected();
  };
}
